import { useCallback, useEffect, useState } from 'react'
import { ContactCompany } from 'models/companies'
import { EmailMessage } from 'models/emailIntegration'
import { Modal } from 'components/UI/Modal'
import { EmailsListView } from './EmailsListView'
import { LegacyEmailDetailView } from './LegacyEmailDetailView'

type EmailTab = 'received' | 'sent'
type ModalView = 'list' | 'email'

interface LegacyModalHeaderProps {
  companyName?: string
  view: ModalView
  activeTab: EmailTab
  emailSubject?: string
  onBreadcrumbClick?: () => void
  emailOnlyMode?: boolean
}

function LegacyModalHeader({
  companyName,
  view,
  activeTab,
  emailSubject,
  onBreadcrumbClick,
  emailOnlyMode,
}: LegacyModalHeaderProps) {
  const breadcrumbLabel = activeTab === 'received' ? 'Received' : 'Sent'

  if (emailOnlyMode) {
    return (
      <Modal.Header className="px-[32px] py-[20px] border-0">
        <div className="flex flex-col gap-1 w-full">
          {companyName && (
            <div className="text-[11px] font-light uppercase tracking-[0.03em] leading-[28px] align-middle text-[#6E6E6E]">
              {companyName}
            </div>
          )}
          <Modal.Title className="text-2xl font-normal leading-[1.2] tracking-[0em] align-middle text-[#101828] m-0">
            {emailSubject || 'Email'}
          </Modal.Title>
        </div>
      </Modal.Header>
    )
  }

  if (view === 'list') {
    return (
      <Modal.Header className="px-[32px] py-[20px] border-0">
        <div className="flex flex-col gap-1 w-full">
          <div className="text-[11px] font-light uppercase tracking-[0.03em] leading-[28px] align-middle text-[#6E6E6E]">
            {companyName}
          </div>
          <Modal.Title className="text-2xl font-normal leading-[1.2] tracking-[0em] align-middle text-[#101828] m-0">
            Emails
          </Modal.Title>
        </div>
      </Modal.Header>
    )
  }

  return (
    <Modal.Header className="px-[32px] py-[20px] border-0">
      <div className="flex flex-col gap-1 w-full">
        <div className="flex flex-row items-center gap-1 text-[11px] font-light uppercase tracking-[0.03em] leading-[28px] align-middle text-[#6E6E6E]">
          <span>{companyName}</span>
          <span className="text-[#E2E2E2]">|</span>
          <button
            type="button"
            onClick={onBreadcrumbClick}
            className="text-[#2C6B4E] underline hover:text-[#1e4d38] transition-colors"
          >
            {breadcrumbLabel}
          </button>
        </div>
        <Modal.Title className="text-2xl font-normal leading-[1.2] tracking-[0em] align-middle text-[#101828] m-0">
          {emailSubject || 'Email'}
        </Modal.Title>
      </div>
    </Modal.Header>
  )
}

interface BaseEmailsModalProps {
  show: boolean
  onHide: () => void
  forDoorProxyId?: number
  companyName?: string
  initialTab?: EmailTab
}

interface EmailsListModalProps extends BaseEmailsModalProps {
  emailOnlyMode?: false
  contactCompany: ContactCompany
  initialEmail?: EmailMessage
}

interface EmailOnlyModalProps extends BaseEmailsModalProps {
  emailOnlyMode: true
  contactCompany?: never
  initialEmail: EmailMessage
}

type LegacyEmailsModalProps = EmailsListModalProps | EmailOnlyModalProps

export function LegacyEmailsModal(props: LegacyEmailsModalProps) {
  const {
    show,
    onHide,
    forDoorProxyId,
    companyName,
    initialEmail,
    initialTab = 'received',
    emailOnlyMode,
  } = props

  const contactCompany = emailOnlyMode ? undefined : props.contactCompany

  const [view, setView] = useState<ModalView>(
    emailOnlyMode || initialEmail ? 'email' : 'list'
  )
  const [selectedTab, setSelectedTab] = useState<EmailTab>(initialTab)
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | undefined>(
    initialEmail
  )
  const [emailsList, setEmailsList] = useState<EmailMessage[]>([])

  const currentIndex = selectedEmail
    ? emailsList.findIndex((e) => e.id === selectedEmail.id)
    : -1
  const hasPrev = !emailOnlyMode && currentIndex > 0
  const hasNext = !emailOnlyMode && currentIndex < emailsList.length - 1

  const handlePrevEmail = useCallback(() => {
    if (hasPrev) {
      setSelectedEmail(emailsList[currentIndex - 1])
    }
  }, [hasPrev, emailsList, currentIndex])

  const handleNextEmail = useCallback(() => {
    if (hasNext) {
      setSelectedEmail(emailsList[currentIndex + 1])
    }
  }, [hasNext, emailsList, currentIndex])

  const handleEmailsChange = useCallback((emails: EmailMessage[]) => {
    setEmailsList(emails)
  }, [])

  useEffect(() => {
    if (show && initialEmail) {
      setSelectedEmail(initialEmail)
      setView('email')
      setSelectedTab(initialEmail.outbound ? 'sent' : 'received')
    }
  }, [show, initialEmail])

  useEffect(() => {
    if (!show) {
      setView(emailOnlyMode ? 'email' : 'list')
      setSelectedEmail(emailOnlyMode ? initialEmail : undefined)
      setSelectedTab(initialTab)
      setEmailsList([])
    }
  }, [show, initialTab, emailOnlyMode, initialEmail])

  const handleEmailClick = (email: EmailMessage) => {
    setSelectedEmail(email)
    setView('email')
  }

  const handleBreadcrumbClick = () => {
    setView('list')
    setSelectedEmail(undefined)
  }

  const displayName = companyName || contactCompany?.name

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      enforceFocus={false}
      backdrop="static"
    >
      <button
        type="button"
        className="btn-close absolute top-5 right-[30px] z-10"
        aria-label="Close"
        onClick={onHide}
      />
      <LegacyModalHeader
        companyName={displayName}
        view={view}
        activeTab={selectedTab}
        emailSubject={selectedEmail?.subject}
        onBreadcrumbClick={emailOnlyMode ? undefined : handleBreadcrumbClick}
        emailOnlyMode={emailOnlyMode}
      />

      <Modal.Body className="px-[32px] pb-[24px] pt-0">
        <div
          style={{ height: '60vh', display: 'flex', flexDirection: 'column' }}
        >
          {view === 'list' && contactCompany ? (
            <EmailsListView
              contactCompanyId={contactCompany.id}
              forDoorProxyId={forDoorProxyId}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              onEmailClick={handleEmailClick}
              onEmailsChange={handleEmailsChange}
            />
          ) : (
            selectedEmail && (
              <LegacyEmailDetailView
                email={selectedEmail}
                onPrev={hasPrev ? handlePrevEmail : undefined}
                onNext={hasNext ? handleNextEmail : undefined}
              />
            )
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
